import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useChangeProjectBoxes, useProject, useProjectBoxes } from '../api/queries'
import type { CaseBoxLocation, ProjectBox, ProjectBoxMove } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { formatDateTime } from '../lib/format'
import { caseLabelAddress } from './CaseLabel'
import { Avatar, Card, ErrorBox, Loading } from './ui'

/** "I Anders' bil · AB 12 345 (Transit)" eller "På værkstedet". */
function describe(move: ProjectBoxMove, myId?: number | null) {
  if (move.location === 'office') return 'På værkstedet'
  const car = move.vehicleName ? ` · ${move.vehicleName}` : ''
  if (move.user && move.user.id === myId) return `I din bil${car}`
  const name = move.user?.displayName
  // Dansk ejefald: "Anders' bil", "Birgittes bil".
  const owner = !name ? 'en montørs' : /[sxz]$/i.test(name) ? `${name}'` : `${name}s`
  return `I ${owner} bil${car}`
}

/** "Kasse 2 · Kabler" */
export function boxTitle(box: Pick<ProjectBox, 'number' | 'name'>) {
  return `Kasse ${box.number}${box.name ? ` · ${box.name}` : ''}`
}

/**
 * Sagskasserne (beslutning 144 og 145). En sag kan have flere kasser med hver sit mærkat;
 * montøren scanner en kasse og trykker "Med i min bil" eller "Sat af på værkstedet".
 * Kommer man fra QR-koden (`highlight` = kassens nummer), rulles kortet frem og fremhæves.
 */
export function CaseBoxCard({ projectId, highlight }: { projectId: number; highlight?: number | null }) {
  const { user } = useAuth()
  const { data, isLoading, error } = useProjectBoxes(projectId)
  const change = useChangeProjectBoxes(projectId)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (highlight && !isLoading) ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [highlight, isLoading])

  if (isLoading) return null
  if (error) return <ErrorBox error={error} />

  const boxes = data ?? []

  return (
    <div ref={ref} className={highlight ? 'case-box case-box--highlight' : 'case-box'}>
      <Card
        title={boxes.length > 1 ? `📦 Sagskasser (${boxes.length})` : '📦 Sagskassen'}
        actions={
          <div className="btn-group">
            <Link className="btn btn--sm" to={`/projects/${projectId}/label`}>
              🏷️ Mærkater
            </Link>
            <button
              className="btn btn--sm"
              disabled={change.isPending}
              onClick={() => {
                const name = prompt('Navn på den nye kasse (kan stå tomt), f.eks. "Kabler":')
                if (name !== null) change.mutate({ kind: 'add', name: name.trim() || undefined })
              }}
            >
              + Tilføj kasse
            </button>
          </div>
        }
      >
        <div className="stack" style={{ gap: 0 }}>
          {boxes.map((box) => (
            <BoxRow
              key={box.number}
              box={box}
              myId={user?.id}
              highlighted={highlight === box.number && boxes.length > 1}
              busy={change.isPending}
              onMove={(location) => change.mutate({ kind: 'move', number: box.number, location })}
              onRename={() => {
                const name = prompt(`Navn på kasse ${box.number} (tomt fjerner navnet):`, box.name ?? '')
                if (name !== null) change.mutate({ kind: 'rename', number: box.number, name: name.trim() })
              }}
              onRemove={() => {
                if (confirm(`Fjern ${boxTitle(box)} fra sagen? Mærkatet på kassen virker så ikke længere.`))
                  change.mutate({ kind: 'remove', number: box.number })
              }}
            />
          ))}
        </div>
        {change.error && <ErrorBox error={change.error} />}
      </Card>
    </div>
  )
}

function BoxRow({
  box,
  myId,
  highlighted,
  busy,
  onMove,
  onRename,
  onRemove
}: {
  box: ProjectBox
  myId?: number | null
  highlighted: boolean
  busy: boolean
  onMove: (location: CaseBoxLocation) => void
  onRename: () => void
  onRemove: () => void
}) {
  const [showHistory, setShowHistory] = useState(false)
  const current = box.current
  const mine = current?.location === 'vehicle' && current.user?.id === myId
  const atWorkshop = current?.location === 'office'

  return (
    <div className={`case-box__row ${highlighted ? 'case-box__row--on' : ''}`}>
      <div className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {current?.location === 'vehicle' ? (
          <Avatar user={current.user} size={32} />
        ) : (
          <span style={{ fontSize: 26, lineHeight: 1 }} aria-hidden>
            {atWorkshop ? '🏢' : '📦'}
          </span>
        )}
        <div style={{ flex: 1, minWidth: 160 }}>
          <div className="tiny muted" style={{ fontWeight: 700 }}>
            {boxTitle(box)}
          </div>
          {current ? (
            <>
              <div style={{ fontWeight: 700 }}>{describe(current, myId)}</div>
              <div className="tiny muted">
                {current.user?.displayName ?? 'Ukendt'} · {formatDateTime(current.createdAt)}
              </div>
            </>
          ) : (
            <div className="small muted">Ikke scannet endnu.</div>
          )}
        </div>
      </div>

      <div className="btn-group" style={{ marginTop: 8 }}>
        <button className={`btn btn--sm ${mine ? '' : 'btn--primary'}`} disabled={busy || mine} onClick={() => onMove('vehicle')}>
          🚐 Med i min bil
        </button>
        <button
          className={`btn btn--sm ${atWorkshop ? '' : 'btn--primary'}`}
          disabled={busy || atWorkshop}
          onClick={() => onMove('office')}
        >
          🏢 Sat af på værkstedet
        </button>
        <button className="btn btn--sm" disabled={busy} onClick={onRename}>
          Navn
        </button>
        {box.history.length > 1 && (
          <button className="btn btn--sm" onClick={() => setShowHistory(!showHistory)}>
            {showHistory ? 'Skjul' : 'Hvor har den været?'}
          </button>
        )}
        {box.canDelete && (
          <button className="btn btn--sm btn--danger" disabled={busy} onClick={onRemove}>
            Fjern
          </button>
        )}
      </div>

      {showHistory && (
        <ul className="small" style={{ margin: '8px 0 0', paddingLeft: 18 }}>
          {box.history.slice(1).map((m) => (
            <li key={m.id}>
              {describe(m, myId)} · {m.user?.displayName ?? 'Ukendt'}, {formatDateTime(m.createdAt)}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Sag og kasse fra sagskassens QR-kode: ".../s/123" er kasse 1 (mærkater fra før
 * beslutning 145), ".../s/123/2" er kasse 2. Ellers null.
 */
export function caseBoxFromScan(text: string): { projectId: number; number: number } | null {
  const match = /\/s\/(\d+)(?:\/(\d+))?\/?(?:[?#].*)?$/.exec(text.trim())
  return match ? { projectId: Number(match[1]), number: match[2] ? Number(match[2]) : 1 } : null
}

/**
 * Valget efter en scanning af en sagskasse i appens scanner ("+" → Scan): hvilken sag og
 * kasse det er, hvor den er nu, og de to knapper. Bagefter kan man scanne den næste.
 */
export function CaseBoxChoice({
  projectId,
  number,
  onScanNext
}: {
  projectId: number
  number: number
  onScanNext: () => void
}) {
  const { user } = useAuth()
  const project = useProject(projectId)
  const boxes = useProjectBoxes(projectId)
  const change = useChangeProjectBoxes(projectId)
  const [moved, setMoved] = useState<CaseBoxLocation | null>(null)

  if (project.isLoading || boxes.isLoading) return <Loading />
  if (project.error) return <ErrorBox error={project.error} />
  if (!project.data) return null

  const p = project.data.project
  const box = boxes.data?.find((b) => b.number === number)
  const address = caseLabelAddress(project.data)

  const choose = (location: CaseBoxLocation) =>
    change.mutate({ kind: 'move', number, location }, { onSuccess: () => setMoved(location) })

  return (
    <div className="case-box case-box--highlight">
      <Card title="📦 Sagskassen">
        <div style={{ fontWeight: 800, fontSize: 18 }}>{p.orderNumber ? `Ordre ${p.orderNumber}` : p.name}</div>
        <div className="small" style={{ fontWeight: 700 }}>
          {boxTitle(box ?? { number, name: null })}
        </div>
        <div className="small">{p.name}</div>
        {address && <div className="small muted">{address}</div>}

        {!box ? (
          <div className="error-box" style={{ marginTop: 12 }}>
            Kasse {number} findes ikke på sagen - den kan være fjernet. Mærkatet virker ikke længere.
          </div>
        ) : moved ? (
          <div className="small" style={{ marginTop: 12, fontWeight: 700, color: 'var(--success)' }}>
            ✓ {moved === 'vehicle' ? 'Kassen er nu i din bil' : 'Kassen er sat af på værkstedet'}
          </div>
        ) : (
          <>
            {box.current && (
              <div className="tiny muted" style={{ marginTop: 8 }}>
                Nu: {describe(box.current, user?.id)} · {box.current.user?.displayName ?? 'Ukendt'},{' '}
                {formatDateTime(box.current.createdAt)}
              </div>
            )}
            <div className="btn-group" style={{ marginTop: 12 }}>
              <button className="btn btn--primary" disabled={change.isPending} onClick={() => choose('vehicle')}>
                🚐 Med i min bil
              </button>
              <button className="btn btn--primary" disabled={change.isPending} onClick={() => choose('office')}>
                🏢 Sat af på værkstedet
              </button>
            </div>
          </>
        )}
        {change.error && <ErrorBox error={change.error} />}

        <div className="btn-group" style={{ marginTop: 12 }}>
          <button className="btn btn--sm" onClick={onScanNext}>
            📷 Scan næste
          </button>
          <Link className="btn btn--sm" to={`/projects/${projectId}`}>
            Åbn sagen
          </Link>
        </div>
      </Card>
    </div>
  )
}
